import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

const reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

// Hero-parallax, twee soorten:
//   [data-parallax-out]   — foto wordt tijdens het wegscrollen vanuit het
//                           midden van de hero naar buiten gedrukt (zie
//                           getOutward), maar nooit helemaal uit beeld.
//                           Met data-parallax-rise="0.06" gaat 'ie verticaal
//                           juist omhoog (rise × hero-hoogte) i.p.v. naar
//                           beneden — de onderste rij, zodat die de bol in
//                           het volgende scherm niet raakt.
//   [data-parallax-speed] — verticaal: speed × hero-hoogte omhoog; negatief
//                           blijft juist achter (de tekst).
// Loopt via de CSS translate-property (--parallax-x/-y), zodat het niet botst
// met de transform van hero-reveal.js.

// Minimale duw: deel van de (gemiddelde) fotomaat. Foto's die in Figma al op
// de rand staan, schuiven zo toch zichtbaar mee naar buiten.
const MIN_PUSH = 0.5;
// Deel van de foto dat altijd binnen de hero blijft.
const MIN_VISIBLE = 0.15;

// Verschuiving van `item` langs de lijn vanuit het hero-midden: tot z'n
// middelpunt op de rand ligt, minstens MIN_PUSH, en hooguit tot er nog
// MIN_VISIBLE van over is. Layout-maten, dus los van lopende transforms.
function getOutward( hero, item ) {
	let x = 0;
	let y = 0;
	for ( let el = item; el && el !== hero; el = el.offsetParent ) {
		x += el.offsetLeft;
		y += el.offsetTop;
	}

	const width = hero.offsetWidth;
	const height = hero.offsetHeight;
	const w = item.offsetWidth;
	const h = item.offsetHeight;
	const cx = x + w / 2;
	const cy = y + h / 2;
	const length = Math.hypot( cx - width / 2, cy - height / 2 ) || 1;
	const ux = ( cx - width / 2 ) / length;
	const uy = ( cy - height / 2 ) / length;

	// Afstand (langs u) tot een grens per as; Infinity als u die as niet raakt.
	const along = ( u, toPositive, toNegative ) => ( u > 0 ? toPositive / u : u < 0 ? toNegative / -u : Infinity );

	const toEdge = Math.max( 0, Math.min(
		along( ux, width - cx, cx ),
		along( uy, height - cy, cy )
	) );
	const cap = Math.min(
		along( ux, width - x - MIN_VISIBLE * w, x + w - MIN_VISIBLE * w ),
		along( uy, height - y - MIN_VISIBLE * h, y + h - MIN_VISIBLE * h )
	);
	const distance = Math.max( 0, Math.min( Math.max( toEdge, MIN_PUSH * ( w + h ) / 2 ), cap ) );

	return { x: ux * distance, y: uy * distance };
}

function initHeroParallax( hero ) {
	const outward = [ ...hero.querySelectorAll( '[data-parallax-out]' ) ];
	const vertical = [ ...hero.querySelectorAll( '[data-parallax-speed]' ) ];
	if ( ! outward.length && ! vertical.length ) {
		return;
	}

	const tl = gsap.timeline( {
		defaults: { ease: 'none' },
		scrollTrigger: {
			trigger: hero,
			start: 'top top',
			// Uitgedrukt als de hero nog een derde in beeld is.
			end: 'bottom 30%',
			scrub: true,
			invalidateOnRefresh: true,
		},
	} );

	outward.forEach( ( item ) => {
		const rise = parseFloat( item.dataset.parallaxRise );
		tl.fromTo(
			item,
			{ '--parallax-x': '0px', '--parallax-y': '0px' },
			{
				'--parallax-x': () => `${ getOutward( hero, item ).x }px`,
				'--parallax-y': () => `${ Number.isNaN( rise ) ? getOutward( hero, item ).y : -rise * hero.offsetHeight }px`,
			},
			0
		);
	} );

	vertical.forEach( ( item ) => {
		const speed = parseFloat( item.dataset.parallaxSpeed ) || 0;
		tl.fromTo(
			item,
			{ '--parallax-y': '0px' },
			{ '--parallax-y': () => `${ -speed * hero.offsetHeight }px` },
			0
		);
	} );
}

function initHeroParallaxes() {
	if ( reduceMotion ) {
		return;
	}
	document.querySelectorAll( '[data-hero-parallax]' ).forEach( initHeroParallax );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initHeroParallaxes );
} else {
	initHeroParallaxes();
}
